import React, {useState} from 'react';
import {useNavigate} from "react-router-dom";
import Box from '@mui/material/Box';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {useForm} from "../../_utils";
import {useAuth} from "../../_auth/useAuth";
import {useLocation} from "react-router-dom";
import {useTheme} from "@mui/material/styles";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import TextField from "@mui/material/TextField";
import {AuthPasswordLoginOptions} from "../../_auth/types";
import {ForgotPasswordLink, Form, LoginPaper, SignUpButton, SubmitButton} from "./Login.styles";
import {Location} from "history";
import {customerApi} from "../../_api";
import {IAlertProps} from "../_shared";
import {RoutePaths} from "../../App/Routes/routePaths";
import Link from "@mui/material/Link";

interface loginState {
    returnTo: Location
};

export const LoginPasswordView: React.FC = () => {
    const theme = useTheme();
    const { state } = useLocation();
    const { loginWithPassword, toggleNavigation, getAccessToken } = useAuth();
    const navigate = useNavigate();
    const [alertProps, setAlertProps] = useState<IAlertProps>({message: ""});
    const [showError, setShowError] = useState<boolean>(false);


    // defining the initial state for the form
    const initialValues = {
        email: '',
        password: '',
    };

    const showAlertPanel = ({title, message, type="error"}: IAlertProps) => {
        const msgProps: IAlertProps = {
            message: message,
            title: title,
            type: type
        }
        setAlertProps(msgProps);
        setShowError(true);
    }

    // get event handlers from our custom form hook
    const { onChange, onSubmit, formValues } = useForm(
        loginCallback,
        initialValues
    );

    // a submit function that will execute upon form submission
    async function loginCallback() {
        setShowError(false);

        // Create appState object including email.
        // Email will be used to get the appropriate auth provider.
        // Add appState object to formValues object
        const myState = state as loginState;
        const appState = {
            "email": formValues.email,
            "returnTo": myState?.returnTo,
        };
        formValues.appState = appState;

        // Call Auth Api - loginWithRedirect()
        // get authorize uri based on user email address
        // redirect to appropriate login uri.
        const options: AuthPasswordLoginOptions = formValues;

        if (loginWithPassword) {
            try {
                const resp = await loginWithPassword(options);

                // Handle NEW_PASSWORD_REQUIRED challenge - redirect to set new password page
                if (resp && resp.state === "new_password_required") {
                    navigate("/set-new-password", {
                        replace: true,
                        state: {
                            session: resp.session,
                            username: resp.username,
                            email: formValues.email,
                        }
                    });
                    return;
                }

                if (resp && resp.state === "success") {
                    if (resp?.user?.privileges?.includes("Licensee") || resp?.user?.privileges?.includes("SystemAdmin")) {
                        navigate("/", {replace: true});
                        return;
                    }
                    const auth_token = await getAccessToken();
                    const customerResp = await customerApi.customers_domain_exists_by_email({email: formValues.email}, auth_token);
                    if (customerResp && customerResp.data) {
                        navigate(RoutePaths.PAFS, {replace: true});
                    } else {
                        await toggleNavigation();
                        navigate(RoutePaths.COMPANY_REGISTER, {replace: true});
                    }
                } else {
                    showAlertPanel({
                        title: "Error: Login Failed",
                        message: resp.error!,
                        type: "error"
                    });
                }
            } catch {
                showAlertPanel({
                    title: "API Error: Login Failed",
                    message: "An error occurred while performing Login. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action.",
                    type: "error"
                });
            }
        }
    }

    const handleSignUp = () => {
        navigate("/signup");
    }

    return (
        <>
            <LoginPaper elevation={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <AccountCircleIcon
                        sx={{
                            fontSize: 68,
                            color: theme.palette.grey["500"],
                            mb: 2
                        }}
                    />
                    <Typography variant="h4">Sign In</Typography>
                </Box>
                <Grid>
                    {showError ? (
                        <Alert severity={alertProps.type}>
                            <AlertTitle>{alertProps.title}</AlertTitle>
                            {alertProps.message}
                        </Alert>
                    ) : null}
                    <Form
                        autoComplete="off"
                        onSubmit={onSubmit}
                    >
                        <TextField
                            required
                            id="email"
                            name="email"
                            label="Email Address"
                            type="email"
                            margin="normal"
                            color="primary"
                            onChange={onChange}
                        />
                        <TextField
                            required
                            id="password"
                            name="password"
                            label="Password"
                            type="password"
                            margin="normal"
                            color="primary"
                            onChange={onChange}
                        />
                        <SubmitButton
                            type="submit"
                            variant="contained"
                        >
                            Sign In
                        </SubmitButton>
                    </Form>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Link
                            component={ForgotPasswordLink}
                            to="/password-reset"
                        >
                            Forgot password?
                        </Link>
                    </Box>
                    <hr/>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <SignUpButton
                            type="button"
                            variant="contained"
                            onClick={handleSignUp}
                        >
                            Create an Account
                        </SignUpButton>
                    </Box>
                </Grid>
            </LoginPaper>
        </>
    );
}